/**
 * Build Version Checker Utility
 * 
 * This utility provides functions to check for build version mismatches
 * between the server-served HTML and the client-side JavaScript.
 * When a mismatch is detected, it forces a page reload to ensure
 * consistency and avoid broken pages due to asset mismatches.
 */

/**
 * Checks if there's a build version mismatch between server and client
 * @returns {boolean} true if mismatch detected, false otherwise
 */
export const checkBuildVersionMismatch = () => {
    // Get build ID from meta tag (server-side)
    const metaBuildId = document.querySelector('meta[name="build-id"]')?.getAttribute('content');

    // Get build ID from window object (client-side)
    const windowBuildId = window.__BUILD_ID__;

    // If either is missing, we can't perform the check
    if (!metaBuildId || !windowBuildId) {
        console.warn('🔍 Build version check skipped: missing build ID data');
        return false;
    }

    // Check for mismatch
    const hasMismatch = metaBuildId !== windowBuildId;

    if (hasMismatch) {
        console.warn('🚨 Build version mismatch detected!');
        console.warn(`Server build ID: ${metaBuildId}`);
        console.warn(`Client build ID: ${windowBuildId}`);
    }

    return hasMismatch;
};

/**
 * Performs a hard reload of the page
 * This will fetch the latest version from the server
 */
export const performHardReload = () => {
    console.log('🔄 Performing hard reload to sync with latest version...');

    // Use location.reload(true) for hard reload (bypasses cache)
    // Note: The boolean parameter is deprecated but still works in most browsers
    try {
        window.location.reload(true);
    } catch (error) {
        // Fallback for browsers that don't support the boolean parameter
        window.location.reload();
    }
};

/**
 * Checks for build version mismatch and reloads if necessary
 * This is the main function to call for version checking
 */
export const checkAndReloadIfNeeded = () => {
    if (checkBuildVersionMismatch()) {
        // Add a small delay to allow logging to complete
        setTimeout(() => {
            performHardReload();
        }, 100);
        return true;
    }
    return false;
};

/**
 * Custom hook for build version checking
 * @param {Object} options - Configuration options
 * @param {boolean} options.checkOnMount - Whether to check on component mount
 * @param {boolean} options.checkOnRouteChange - Whether to check on route changes
 * @returns {Object} - Object with check functions
 */
export const useBuildVersionChecker = (options = {}) => {
    // const { 
    //     checkOnMount = true, 
    //     checkOnRouteChange = true 
    // } = options;

    return {
        checkVersion: checkAndReloadIfNeeded,
        checkMismatch: checkBuildVersionMismatch,
        hardReload: performHardReload
    };
};